import {
  GetServiceResourcesForBookingToolDocument,
  GetServiceResourcesForBookingToolQuery,
  GetServiceResourcesForBookingToolQueryVariables,
} from "../GeneratedGraphQL";
import { testResourceGroup1, testResourceGroup2 } from "./resourceGroup";

import { createPageInfo } from "./pageInfo";
import { defaultBookingTool } from "./bookingTool";
import { defaultService } from "./service";
import { MockedResponse } from "@apollo/client/testing";
import { Resource } from "../AppDataProvider/types";

export const testResource1: Resource = {
  _id: "aaaaaaaaaaaaaaaaaaaaaaaa",
  description: "This is a basic test resource.",
  groups: {
    edges: [{ cursor: testResourceGroup1._id, node: testResourceGroup1 }],
    pageInfo: createPageInfo(),
  },
  name: "Test Resource 1",
  profilePhotoUrl: "https://i.redd.it/7wfphr6b0gd41.jpg",
};
export const testResource2: Resource = {
  _id: "aaaaaaaaaaaaaaaaaaaaaaab",
  description:
    "This is another test resource. It has <strong>strange and dangerous</strong> markup in its description.",
  groups: {
    edges: [
      { cursor: testResourceGroup1._id, node: testResourceGroup1 },
      { cursor: testResourceGroup2._id, node: testResourceGroup2 },
    ],
    pageInfo: createPageInfo({ total: 2 }),
  },
  name: "Test Resource 2",
  profilePhotoUrl: "https://i.redd.it/hqevpz4ez1y01.jpg",
};
export const testResource3: Resource = {
  _id: "aaaaaaaaaaaaaaaaaaaaaaac",
  groups: {
    edges: [{ cursor: testResourceGroup2._id, node: testResourceGroup2 }],
    pageInfo: createPageInfo(),
  },
  name: "Test Resource 3 (no description)",
  profilePhotoUrl: "https://i.imgur.com/nG9T4Pi.jpeg",
};
export const testResource4: Resource = {
  _id: "aaaaaaaaaaaaaaaaaaaaaaad",
  description: "This is the fourth test resource. It has no profile photo.",
  groups: { edges: [], pageInfo: createPageInfo({ total: 0 }) },
  name: "Test Resource 4",
};
export const testResource5: Resource = {
  _id: "aaaaaaaaaaaaaaaaaaaaaaae",
  description:
    "It was a bright cold day in April, and the clocks were striking thirteen. Winston Smith, his chin nuzzled into his breast in an effort to escape the vile wind, slipped quickly through the glass doors of Victory Mansions, though not quickly enough to prevent a swirl of gritty dust from entering along with him.",
  groups: { edges: [], pageInfo: createPageInfo({ total: 0 }) },
  name: "Test Resource 5 (title only)",
  profilePhotoUrl: "https://i.redd.it/mcq393s4lgbc1.png",
};

export const defaultResource = testResource1;
export const multipleResources = [
  testResource1,
  testResource2,
  testResource3,
  testResource4,
  testResource5,
];
export const multipleResourcesWithGroups = [
  testResource1,
  testResource2,
  testResource3,
];

const defaultResourceRequestVariables: GetServiceResourcesForBookingToolQueryVariables =
  { bookingToolId: defaultBookingTool._id, id: defaultService._id };
export const defaultResourceRequest: MockedResponse<GetServiceResourcesForBookingToolQuery> =
  {
    request: {
      query: GetServiceResourcesForBookingToolDocument,
      variables: defaultResourceRequestVariables,
    },
    result: {
      data: {
        service: {
          resourcesForBookingTool: {
            edges: multipleResources.map((resource) => ({
              cursor: resource._id,
              node: resource,
            })),
            pageInfo: createPageInfo({ total: 5 }),
          },
        },
      },
    },
  };
export const singleResourceRequest: MockedResponse<GetServiceResourcesForBookingToolQuery> =
  {
    request: {
      query: GetServiceResourcesForBookingToolDocument,
      variables: defaultResourceRequestVariables,
    },
    result: {
      data: {
        service: {
          resourcesForBookingTool: {
            edges: [{ cursor: defaultResource._id, node: defaultResource }],
            pageInfo: createPageInfo({ total: 1 }),
          },
        },
      },
    },
  };
export const noResourceGroupsResourceRequest: MockedResponse<GetServiceResourcesForBookingToolQuery> =
  {
    request: {
      query: GetServiceResourcesForBookingToolDocument,
      variables: defaultResourceRequestVariables,
    },
    result: {
      data: {
        service: {
          resourcesForBookingTool: {
            edges: [testResource4, testResource5].map((resource) => ({
              cursor: resource._id,
              node: resource,
            })),
            pageInfo: createPageInfo({ total: 5 }),
          },
        },
      },
    },
  };
export const resourceIdsResourceRequest: MockedResponse<GetServiceResourcesForBookingToolQuery> =
  {
    request: {
      query: GetServiceResourcesForBookingToolDocument,
      variables: {
        ...defaultResourceRequestVariables,
        resourceIds: [defaultResource._id],
      },
    },
    result: {
      data: {
        service: {
          resourcesForBookingTool: {
            edges: [{ cursor: defaultResource._id, node: defaultResource }],
            pageInfo: createPageInfo({ total: 1 }),
          },
        },
      },
    },
  };
export const genericErrorResourceRequest: MockedResponse<GetServiceResourcesForBookingToolQuery> =
  {
    error: new Error("That's not very resourceful of you..."),
    request: {
      query: GetServiceResourcesForBookingToolDocument,
      variables: defaultResourceRequestVariables,
    },
  };
export const noServiceResourceRequest: MockedResponse<GetServiceResourcesForBookingToolQuery> =
  {
    request: {
      query: GetServiceResourcesForBookingToolDocument,
      variables: defaultResourceRequestVariables,
    },
    result: { data: { service: null } },
  };
export const noResourcesRequest: MockedResponse<GetServiceResourcesForBookingToolQuery> =
  {
    request: {
      query: GetServiceResourcesForBookingToolDocument,
      variables: defaultResourceRequestVariables,
    },
    result: {
      data: {
        service: {
          resourcesForBookingTool: {
            edges: [],
            pageInfo: createPageInfo({ total: 0 }),
          },
        },
      },
    },
  };
export const multiPageResourcesRequests: MockedResponse<GetServiceResourcesForBookingToolQuery>[] =
  multipleResources.map((resource, index) => ({
    request: {
      query: GetServiceResourcesForBookingToolDocument,
      variables: {
        ...defaultResourceRequestVariables,
        ...(index !== 0 && { cursor: multipleResources[index - 1]._id }),
      },
    },
    result: {
      data: {
        service: {
          resourcesForBookingTool: {
            edges: [{ cursor: resource._id, node: resource }],
            /* eslint-disable sort-keys */
            pageInfo: {
              total: multipleResources.length,
              hasNextPage: index !== multipleResources.length - 1,
              hasPreviousPage: index !== 0,
              startCursor: resource._id,
              endCursor: resource._id,
            },
            /* eslint-enable sort-keys */
          },
        },
      },
    },
  }));
